/** 키워드 탭 — an app's keywords (S3, `/apps/:id/keywords`).
 *
 *  What the old Keywords tab did in per-country card grids is one table here:
 *  filters in the address (country, sort and direction, search, unanalyzed
 *  only, page), 100 rows a page on the client, a row opens the keyword drawer
 *  (`useOpenKeyword`), delete asks first. Above the table: the add form and the
 *  two bulk actions (ASC import, analyze the never-analyzed); below it, folded,
 *  the keyword suggestions.
 *
 *  Two things go out to other services and are never pressed in a dev server:
 *  analyze (iTunes) and the ASC import. */

import { useMemo, useState } from 'react'
import { ArrowDown, ArrowUp, CloudDownload, Plus, RefreshCw, ScanSearch, Trash2 } from 'lucide-react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { api, type App, type KeywordRow } from '@/api/client'
import { useOpenKeyword } from '@/lib/detail'
import { formatDay } from '@/lib/time'
import { asSort, countsByCountry, keywordTrend, needsAnalysis, shownRank, sortKeywords, SORT_OPTIONS, type SortDir } from '@/lib/keywords'
import { opportunityTier } from '@/lib/opportunity'
import { invalidateKeywordData, qk, useKeywords } from '@/lib/queries'
import { cn } from '@/lib/utils'
import {
  Button,
  ConfirmDialog,
  CountryTag,
  DataTable,
  Delta,
  EmptyState,
  errorText,
  Field,
  FilterBar,
  FilterSelect,
  IconButton,
  Input,
  MiniBar,
  Pager,
  SearchInput,
  Sparkline,
  StatusDot,
  TierPill,
  useQueryParam,
  useSetQueryParams,
  useToast,
  type Column,
} from '@/ui'
import { SuggestPanel } from './SuggestPanel'

export const KEYWORD_PAGE_SIZE = 100

interface Progress {
  currentId: number | null
  currentTerm: string
  done: number
  total: number
  errors: number
}

export function KeywordsTab({ app }: { app: App }) {
  const appId = app.id
  const keywords = useKeywords(appId)
  const qc = useQueryClient()
  const toast = useToast()
  const open = useOpenKeyword()
  const setParams = useSetQueryParams()

  const [country] = useQueryParam('country', 'all')
  const [sortParam] = useQueryParam('sort', 'opportunity')
  const [dirParam] = useQueryParam('dir', 'desc')
  const [q] = useQueryParam('q')
  const [onlyNew] = useQueryParam('unanalyzed')
  const [withRetired] = useQueryParam('retired')
  const [pageParam] = useQueryParam('page', '1')
  const sort = asSort(sortParam)
  const dir: SortDir = dirParam === 'asc' ? 'asc' : 'desc'
  const page = Math.max(1, Number.parseInt(pageParam, 10) || 1)

  // A term that left the listing stays for its history, out of the way.
  const all = useMemo(() => keywords.data?.items ?? [], [keywords.data])
  const retiredCount = useMemo(() => all.filter((k) => k.retired_at).length, [all])
  const items = useMemo(() => (withRetired ? all : all.filter((k) => !k.retired_at)), [all, withRetired])
  const counts = useMemo(() => countsByCountry(items), [items])
  // A country in the address that the app has no keywords for is no filter.
  const filterCountry = country !== 'all' && counts.has(country) ? country : 'all'
  // With a country chosen the add form is locked to it: typing keywords while
  // looking at KR and having them land in US would be a surprise.
  const lockedCountry = filterCountry !== 'all' ? filterCountry : ''

  // ── add ───────────────────────────────────────────────────────────────────
  const [term, setTerm] = useState('')
  const [tag, setTag] = useState('')
  const [addCountry, setAddCountry] = useState('')
  const addKw = useMutation({
    mutationFn: async (terms: string[]) => {
      const c = lockedCountry || addCountry || app.country || 'us'
      const locale = app.primary_locale || 'en-US'
      for (const t of terms) {
        await api.createKeyword(appId, { term: t, country: c, locale, tag, source: 'user', is_tracking: true })
      }
      return terms.length
    },
    onSuccess: (n) => {
      setTerm('')
      setTag('')
      toast.success(`키워드 ${n}개를 추가했다`)
    },
    onError: (e) => toast.error(errorText(e)),
    // Some of the terms may have gone in before one failed.
    onSettled: () => invalidateKeywordData(qc, appId),
  })
  const submitAdd = () => {
    const terms = Array.from(new Set(term.split(',').map((s) => s.trim()).filter(Boolean)))
    if (terms.length) addKw.mutate(terms)
  }

  // ── analyze ───────────────────────────────────────────────────────────────
  const [pendingId, setPendingId] = useState<number | null>(null)
  const analyzeOne = useMutation({
    mutationFn: (kid: number) => api.analyzeKeyword(appId, kid),
    onMutate: (kid) => setPendingId(kid),
    onSuccess: (r, kid) => {
      invalidateKeywordData(qc, appId, kid)
      toast.success(`분석했다. 인기도 ${r.popularity} · 난이도 ${r.difficulty}`)
    },
    onError: (e) => toast.error(errorText(e)),
    onSettled: () => setPendingId(null),
  })

  // The loop runs here, one keyword at a time, so the screen can say which one
  // it is on, honour the country filter, and fill rows in as it goes. The batch
  // endpoint blocks until it is done and cannot report progress.
  const [progress, setProgress] = useState<Progress | null>(null)
  const targets = useMemo(
    () => items.filter((k) => needsAnalysis(k) && (filterCountry === 'all' || k.country === filterCountry)),
    [items, filterCountry],
  )
  const runAnalyzeAll = async () => {
    if (progress || targets.length === 0) return
    const list = targets
    let errors = 0
    setProgress({ currentId: null, currentTerm: '', done: 0, total: list.length, errors })
    for (let i = 0; i < list.length; i++) {
      setProgress({ currentId: list[i].id, currentTerm: list[i].term, done: i, total: list.length, errors })
      try {
        await api.analyzeKeyword(appId, list[i].id)
        qc.invalidateQueries({ queryKey: qk.keywords(appId) })
      } catch {
        errors += 1
      }
    }
    setProgress(null)
    invalidateKeywordData(qc, appId)
    if (errors > 0) toast.error(`${list.length - errors}개를 분석했고 ${errors}개는 실패했다`)
    else toast.success(`${list.length}개를 분석했다`)
  }

  const syncASC = useMutation({
    mutationFn: () => api.syncASCKeywords(appId),
    onSuccess: (r) => {
      invalidateKeywordData(qc, appId)
      toast.success(`ASC에서 키워드 ${r.imported}개를 가져왔다 (로케일 ${r.locales}개)`)
    },
    onError: (e) => toast.error(errorText(e)),
  })

  // ── delete ────────────────────────────────────────────────────────────────
  const [toDelete, setToDelete] = useState<KeywordRow | null>(null)
  const delKw = useMutation({
    mutationFn: (kid: number) => api.deleteKeyword(kid),
    onSuccess: () => {
      invalidateKeywordData(qc, appId)
      toast.success('키워드를 삭제했다')
      setToDelete(null)
    },
    onError: (e) => toast.error(errorText(e)),
  })

  // ── the table ─────────────────────────────────────────────────────────────
  const matching = useMemo(() => {
    const needle = q.trim().toLowerCase()
    return items.filter(
      (k) =>
        (filterCountry === 'all' || (k.country || 'us').toLowerCase() === filterCountry) &&
        (!needle || k.term.toLowerCase().includes(needle)) &&
        (!onlyNew || needsAnalysis(k)),
    )
  }, [items, filterCountry, q, onlyNew])
  const sorted = useMemo(() => sortKeywords(matching, sort, dir), [matching, sort, dir])
  const pages = Math.max(1, Math.ceil(sorted.length / KEYWORD_PAGE_SIZE))
  const shownPage = Math.min(page, pages)
  const rows = sorted.slice((shownPage - 1) * KEYWORD_PAGE_SIZE, shownPage * KEYWORD_PAGE_SIZE)
  const filtered = !!(q || onlyNew || withRetired || filterCountry !== 'all')
  const unanalyzedAll = items.filter(needsAnalysis).length

  const newestSnapshot = items.reduce((m, k) => ((k.latest?.snapshot_date ?? '') > m ? (k.latest?.snapshot_date ?? '') : m), '')

  const stop = { onClick: (e: { stopPropagation: () => void }) => e.stopPropagation(), onKeyDown: (e: { stopPropagation: () => void }) => e.stopPropagation() }

  const columns: Column<KeywordRow>[] = [
    {
      key: 'term',
      header: '키워드',
      card: 'title',
      cell: (k) => (
        <span className="block min-w-0">
          <span className="block truncate font-medium" title={k.term}>
            {k.term}
          </span>
          {k.tag && <span className="block truncate text-meta text-muted-foreground">{k.tag}</span>}
        </span>
      ),
    },
    { key: 'country', header: '국가', width: '64px', card: 'hidden', cell: (k) => <CountryTag code={k.country} /> },
    {
      key: 'rank',
      header: '순위',
      width: '64px',
      align: 'right',
      card: 'meta',
      className: 'tabular-nums',
      cell: (k) => {
        const rank = shownRank(k)
        if (!(rank > 0)) return <span className="text-muted-foreground">—</span>
        // Bold is the store's own order. Muted is the Search API's, another list.
        return k.store ? (
          <span className="font-semibold" title={`스토어 순서 · ${formatDay(k.store.date)}`}>
            #{rank}
          </span>
        ) : (
          <span className="text-muted-foreground" title="Search API 순서. 스토어 순서는 우선 문구만 읽는다">
            #{rank}
          </span>
        )
      },
    },
    {
      key: 'spark',
      header: '추이',
      width: '112px',
      card: 'line',
      cell: (k) => <Sparkline series={(k.history ?? []).map((p) => p.rank)} dates={(k.history ?? []).map((p) => p.date)} width={96} label={`${k.term} 순위 추이`} />,
    },
    {
      key: 'delta',
      header: '변동',
      width: '96px',
      card: 'hidden',
      cell: (k) => {
        const t = keywordTrend(k.history)
        return t ? <Delta status={t.status} delta={t.delta} /> : <span className="text-muted-foreground">—</span>
      },
    },
    { key: 'pop', header: '인기도', width: '112px', card: 'hidden', cell: (k) => <MiniBar label="인기도" value={k.latest?.popularity ?? 0} fill="bg-chart-1" /> },
    { key: 'diff', header: '난이도', width: '112px', card: 'hidden', cell: (k) => <MiniBar label="난이도" value={k.latest?.difficulty ?? 0} fill="bg-chart-2" /> },
    {
      // the phone's one line for both measures
      key: 'measures',
      header: '인기 · 난이도',
      cardOnly: true,
      cell: (k) => (
        <span className="inline-flex flex-wrap items-center gap-x-4 gap-y-1">
          <MiniBar label="인기도" value={k.latest?.popularity ?? 0} fill="bg-chart-1" />
          <MiniBar label="난이도" value={k.latest?.difficulty ?? 0} fill="bg-chart-2" />
        </span>
      ),
    },
    {
      key: 'tier',
      header: '기회',
      width: '88px',
      card: 'hidden',
      cell: (k) => <TierPill tier={opportunityTier(k.latest?.popularity ?? 0, k.latest?.difficulty ?? 0)} />,
    },
    {
      key: 'summary',
      header: '요약',
      card: 'sub',
      cardOnly: true,
      cell: (k) => {
        const t = keywordTrend(k.history)
        return (
          <span className="inline-flex flex-wrap items-center gap-2">
            <CountryTag code={k.country} />
            <TierPill tier={opportunityTier(k.latest?.popularity ?? 0, k.latest?.difficulty ?? 0)} />
            {t && <Delta status={t.status} delta={t.delta} />}
          </span>
        )
      },
    },
    {
      key: 'updated',
      header: '갱신일',
      width: '104px',
      card: 'line',
      // On a card only when it says something: a keyword being analysed, or
      // one whose last snapshot is older than the rest.
      cardHide: (k) => !(progress?.currentId === k.id || pendingId === k.id) && (k.latest?.snapshot_date ?? '') === newestSnapshot,
      className: 'whitespace-nowrap tabular-nums text-muted-foreground',
      cell: (k) =>
        progress?.currentId === k.id || pendingId === k.id ? (
          <span className="inline-flex items-center gap-1.5 text-accent">
            <StatusDot tone="publishing" pulse />
            분석 중
          </span>
        ) : (
          formatDay(k.latest?.snapshot_date)
        ),
    },
    {
      key: 'actions',
      header: <span className="sr-only">처리</span>,
      width: '88px',
      align: 'right',
      card: 'actions',
      cell: (k) => (
        <span className="inline-flex items-center gap-1.5" {...stop}>
          <IconButton
            icon={RefreshCw}
            size="sm"
            variant="quiet"
            label={`${k.term} 분석`}
            disabled={pendingId !== null || !!progress}
            onClick={() => analyzeOne.mutate(k.id)}
          />
          <IconButton icon={Trash2} size="sm" variant="quietDanger" label={`${k.term} 삭제`} onClick={() => setToDelete(k)} />
        </span>
      ),
    },
  ]

  const countryOptions = [
    { value: 'all', label: `국가 전체 (${items.length})` },
    ...[...counts.entries()].map(([c, n]) => ({ value: c, label: `${c.toUpperCase()} (${n})` })),
  ]
  const analyzeLabel = progress
    ? `분석 중 ${progress.done + 1}/${progress.total}`
    : targets.length === 0
      ? '모두 분석됨'
      : filterCountry === 'all'
        ? `미분석 분석 (${targets.length})`
        : `${filterCountry.toUpperCase()} 미분석 분석 (${targets.length})`

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
        <p className="text-table text-muted-foreground">
          키워드 <span className="tabular-nums text-foreground">{items.length.toLocaleString('ko-KR')}</span>개 · 미분석{' '}
          <span className="tabular-nums text-foreground">{unanalyzedAll.toLocaleString('ko-KR')}</span>
        </p>
        <div className="flex flex-wrap items-center gap-2">
          {app.asc_app_id && (
            <Button
              icon={CloudDownload}
              loading={syncASC.isPending}
              onClick={() => syncASC.mutate()}
              title="App Store Connect에 제출한 키워드 메타데이터를 가져온다"
            >
              ASC에서 가져오기
            </Button>
          )}
          <Button
            variant="primary"
            icon={ScanSearch}
            loading={!!progress}
            disabled={targets.length === 0}
            onClick={runAnalyzeAll}
            title={
              targets.length === 0
                ? '범위 안의 추적 키워드가 모두 인기도와 난이도를 갖고 있다. 스냅샷이 매일 새로 고친다.'
                : `아직 분석하지 않은 키워드 ${targets.length}개를 하나씩 분석한다`
            }
          >
            {analyzeLabel}
          </Button>
        </div>
      </div>

      {progress && (
        <div role="status" aria-label="분석 진행" className="space-y-1.5 rounded-lg border border-border bg-card px-4 py-3 text-table">
          <div className="flex items-center justify-between gap-2">
            <span className="min-w-0 truncate font-medium">
              {progress.done + 1} / {progress.total}
              {progress.currentTerm && <span className="text-muted-foreground"> · {progress.currentTerm}</span>}
            </span>
            {progress.errors > 0 && <span className="shrink-0 text-status-failed">실패 {progress.errors}</span>}
          </div>
          <div
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={progress.total}
            aria-valuenow={progress.done}
            className="h-1.5 overflow-hidden rounded-full bg-muted"
          >
            <div className="h-full bg-chart-1 transition-[width] duration-200" style={{ width: `${Math.round((progress.done / Math.max(progress.total, 1)) * 100)}%` }} />
          </div>
        </div>
      )}

      <form
        aria-label="키워드 추가"
        className="flex flex-wrap items-end gap-2 rounded-lg border border-border bg-card p-4"
        onSubmit={(e) => {
          e.preventDefault()
          submitAdd()
        }}
      >
        <Field label="키워드" hint="쉼표로 여러 개를 한 번에 넣는다" className="w-full sm:w-auto sm:min-w-[14rem] sm:flex-1">
          <Input placeholder="habit tracker, journal" value={term} onChange={(e) => setTerm(e.target.value)} />
        </Field>
        <Field label="태그" className="min-w-0 flex-1 sm:w-32 sm:flex-none">
          <Input placeholder="core" value={tag} onChange={(e) => setTag(e.target.value)} />
        </Field>
        <Field label="국가" hint={lockedCountry ? '국가 필터에 맞춰 고정' : undefined} className="w-24 sm:w-28">
          <Input
            className="uppercase"
            maxLength={2}
            placeholder={(lockedCountry || app.country || 'us').toUpperCase()}
            value={lockedCountry ? lockedCountry.toUpperCase() : addCountry.toUpperCase()}
            disabled={!!lockedCountry}
            title={lockedCountry ? `필터가 ${lockedCountry.toUpperCase()}라 국가가 고정된다. 필터를 전체로 두면 바꿀 수 있다.` : '국가 코드 (us, kr, jp)'}
            onChange={(e) => setAddCountry(e.target.value.toLowerCase())}
          />
        </Field>
        <Button type="submit" icon={Plus} loading={addKw.isPending} disabled={!term.trim()} className="mb-[1px] self-end">
          추가
        </Button>
      </form>

      <FilterBar end={<span className="text-table tabular-nums text-muted-foreground">{sorted.length.toLocaleString('ko-KR')}개</span>}>
        {counts.size > 1 && (
          <FilterSelect label="국가" value={filterCountry} onChange={(v) => setParams({ country: v === 'all' ? '' : v, page: '' })} options={countryOptions} />
        )}
        <FilterSelect
          label="정렬"
          value={sort}
          onChange={(v) => setParams({ sort: v === 'opportunity' ? '' : v, page: '' })}
          options={SORT_OPTIONS.map((o) => ({ value: o.value, label: `정렬: ${o.label}` }))}
        />
        <IconButton
          label={dir === 'desc' ? '정렬 방향: 내림차순' : '정렬 방향: 오름차순'}
          icon={dir === 'desc' ? ArrowDown : ArrowUp}
          size="sm"
          variant="secondary"
          onClick={() => setParams({ dir: dir === 'desc' ? 'asc' : '', page: '' })}
        />
        <SearchInput value={q} onChange={(v) => setParams({ q: v, page: '' })} placeholder="키워드 검색" />
        <label className="inline-flex h-8 cursor-pointer select-none items-center gap-2 text-table">
          <input
            type="checkbox"
            checked={!!onlyNew}
            onChange={(e) => setParams({ unanalyzed: e.target.checked ? '1' : '', page: '' })}
            className="h-4 w-4 accent-accent"
          />
          미분석만
        </label>
        {retiredCount > 0 && (
          <label className="inline-flex h-8 cursor-pointer select-none items-center gap-2 text-table">
            <input
              type="checkbox"
              checked={!!withRetired}
              onChange={(e) => setParams({ retired: e.target.checked ? '1' : '', page: '' })}
              className="h-4 w-4 accent-accent"
            />
            빠진 문구 포함 ({retiredCount.toLocaleString('ko-KR')})
          </label>
        )}
      </FilterBar>

      <DataTable
        label="키워드"
        columns={columns}
        rows={rows}
        rowKey={(k) => k.id}
        loading={keywords.isLoading}
        error={keywords.error}
        onRetry={() => keywords.refetch()}
        onRowClick={(k) => open(appId, k.id)}
        rowClassName={(k) => cn(progress?.currentId === k.id && 'bg-accent/5')}
        empty={
          items.length === 0 ? (
            <EmptyState
              title="키워드가 아직 없다"
              description="위 폼으로 직접 넣거나, 아래 키워드 제안을 열어 후보를 받는다."
            />
          ) : (
            <EmptyState
              title="조건에 맞는 키워드가 없다"
              description="필터를 풀면 더 보인다."
              actions={
                filtered ? (
                  <Button onClick={() => setParams({ country: '', q: '', unanalyzed: '', retired: '', page: '' })}>필터 지우기</Button>
                ) : undefined
              }
            />
          )
        }
      />
      <Pager total={sorted.length} page={shownPage} pageSize={KEYWORD_PAGE_SIZE} onPage={(p) => setParams({ page: p > 1 ? String(p) : '' })} />

      <SuggestPanel app={app} country={lockedCountry || app.country || 'us'} scoped={!!lockedCountry} />

      <ConfirmDialog
        open={toDelete !== null}
        onOpenChange={(o) => !o && setToDelete(null)}
        title={`“${toDelete?.term ?? ''}” 키워드를 삭제할까`}
        description="이 키워드의 순위 이력도 함께 지워진다."
        confirmLabel="삭제"
        confirmVariant="destructive"
        loading={delKw.isPending}
        onConfirm={async () => {
          if (!toDelete) return
          try {
            await delKw.mutateAsync(toDelete.id)
          } catch {
            /* the toast already says why */
          }
        }}
      />
    </div>
  )
}
